import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import moment from 'moment';
import Loader from '../../../../components/ui/Loader/Loader';
import { apiRunWithAxios } from './APIRunWithAxios';

const VoltageGraph = (props) => {
    const [loading, setLoading] = useState(false);
    const [deviceData, setDeviceData] = useState(null);
    const [chartData, setChartData] = useState([]);
    const rootRef = useRef(null);

    const access_token = useSelector(state => state.auth.access_token);

    // Fetch device data
    useEffect(() => {
        if (props.formData?.imeis?.length) {
            fetchDeviceData(
                props.formData.imeis,
                props.formData.date_range_start,
                props.formData.date_range_end
            );
        }
    }, [props.formData]);

    const fetchDeviceData = async (imeis, startDate, endDate) => {
        setLoading(true);
        setDeviceData(null);

        try {
            const requestBody = {
                imeIs: imeis,
                startDate: new Date(startDate).toISOString(),
                endDate: new Date(endDate).toISOString()
            };

            let response = await apiRunWithAxios(
                "https://api6.tappetbox.in/api/DeviceData/get-historical",
                "POST",
                access_token,
                {},
                requestBody
            );

            setDeviceData(response.data);

        } catch (error) {
            toast.error('Failed to fetch device data', { position: toast.POSITION.TOP_RIGHT });
        } finally {
            setLoading(false);
        }
    };

    // Process chart data
    useEffect(() => {
        if (deviceData && Array.isArray(deviceData) && deviceData.length > 0) {
            processChartData(deviceData);
        } else {
            setChartData([]);
        }
    }, [deviceData]);

    // Create chart
    useEffect(() => {
        if (chartData.length > 0) {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }

            const timer = setTimeout(() => {
                createChart();
            }, 100);

            return () => clearTimeout(timer);
        }

        return () => {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }
        };
    }, [chartData]);

    const processChartData = (data) => {
        const filteredData = data
            .map((item) => ({
                date: new Date(item.gpsTime || item.serverTime).getTime(),
                externalVoltage: item.externalVoltage != null ? Number(item.externalVoltage.toFixed(2)) : null,
                batteryVoltage: item.batteryVoltage != null ? Number(item.batteryVoltage.toFixed(2)) : null,
                fuelVoltage: item?.extra?.fuelVoltage != null ? Number(item.extra.fuelVoltage.toFixed(2)) : null,
                formattedDate: moment(item.gpsTime || item.serverTime).format("DD-MMM-YYYY HH:mm:ss")
            }))
            .filter((item) =>
                item.externalVoltage !== null ||
                item.batteryVoltage !== null ||
                item.fuelVoltage !== null
            )
            .sort((a, b) => a.date - b.date);

        setChartData(filteredData);
    };

    const createChart = () => {
        const container = document.getElementById("fuelVolumeChart");
        if (!container) {
            return;
        }

        try {
            const root = am5.Root.new("fuelVolumeChart");
            rootRef.current = root;
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: true,
                    wheelX: "panX",
                    wheelY: "zoomX",
                    pinchZoomX: true
                })
            );

            // X Axis
            const xAxis = chart.xAxes.push(
                am5xy.GaplessDateAxis.new(root, {
                    baseInterval: {
                        timeUnit: "second",
                        count: 30,
                    },
                    renderer: am5xy.AxisRendererX.new(root, {
                        minGridDistance: 90,
                        minorGridEnabled: false,
                    }),
                    tooltip: am5.Tooltip.new(root, {}),
                })
            );
            xAxis.get("renderer").labels.template.setAll({
                rotation: -45,
                centerX: am5.p50,
                centerY: am5.p50,
                dy: 10,
                fontSize: 11
            });

            xAxis.get("dateFormats").second = "HH:mm";
            xAxis.get("dateFormats").minute = "HH:mm";
            xAxis.get("dateFormats").hour = "HH:mm";
            xAxis.get("periodChangeDateFormats").second = "HH:mm";
            xAxis.get("renderer").setAll({
                minGridDistance: 40
            });
            xAxis.setAll({
                startLocation: 0,
                endLocation: 1
            });

            // Y Axis
            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    numberFormat: "#.##"
                })
            );

            yAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
                const value = target.dataItem?.get("value");
                if (value == null) return text;
                return Number(value).toString();
            });

            yAxis.get("renderer").grid.template.setAll({
                stroke: am5.color(0xe0e0e0),
                strokeWidth: 1,
                strokeDasharray: [5, 5]
            });

            xAxis.get("renderer").grid.template.setAll({
                stroke: am5.color(0xe0e0e0),
                strokeWidth: 1,
                strokeDasharray: [5, 5]
            });

            // Fuel Voltage Series
            const fuelVoltageSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name: "Fuel Voltage (V)",
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "fuelVoltage",
                    valueXField: "date",
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "Fuel Voltage: {valueY.formatNumber('#.##')} V\n{formattedDate}"
                    }),
                    stroke: am5.color("#9C27B0"),
                    strokeWidth: 2,
                    connect: true,
                    tensionX: 0.8,
                    minDistance: 5,
                })
            );

            // External Voltage Series
            const externalSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name: "External Voltage (V)",
                    xAxis,
                    yAxis,
                    valueYField: "externalVoltage",
                    valueXField: "date",
                    stroke: am5.color(0x2196F3),
                    strokeWidth: 3,
                    tensionX: 0.8,
                    minDistance: 5,
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "External Voltage: {valueY.formatNumber('#.##')} V\n{formattedDate}"
                    })
                })
            );

            // Battery Voltage Series
            const batterySeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name: "Battery Voltage (V)",
                    xAxis,
                    yAxis,
                    valueYField: "batteryVoltage",
                    valueXField: "date",
                    stroke: am5.color(0x4CAF50),
                    strokeWidth: 3,
                    tensionX: 0.8,
                    minDistance: 5,
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "Battery Voltage: {valueY.formatNumber('#.##')} V\n{formattedDate}"
                    })
                })
            );

            externalSeries.data.setAll(chartData.filter(item => item.externalVoltage !== null));
            batterySeries.data.setAll(chartData.filter(item => item.batteryVoltage !== null));
            fuelVoltageSeries.data.setAll(chartData.filter(item => item.fuelVoltage !== null));

            const cursor = chart.set(
                "cursor",
                am5xy.XYCursor.new(root, {
                    xAxis,
                    behavior: "zoomX"
                })
            );
            cursor.lineY.set("visible", false);

            chart.set("scrollbarX", am5.Scrollbar.new(root, {
                orientation: "horizontal"
            }));

            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.p50,
                    x: am5.p50,
                    layout: root.horizontalLayout,
                    marginTop: 10,
                    marginBottom: 10
                })
            );
            legend.data.setAll([
                externalSeries,
                batterySeries,
                fuelVoltageSeries
            ]);

            chart.appear(1000, 100);
        } catch (error) {
            console.error("Error creating chart:", error);
        }
    };

    if (loading) {
        return (
            <div className="bg-white px-2">
                <Loader />
            </div>
        );
    }

    return (
        <>
            <Helmet><title>Device History</title></Helmet>
            <div className="bg-white px-2">
                <div style={{
                    width: "100%",
                    padding: "20px",
                    background: "#fff",
                    borderRadius: "10px",
                    marginBottom: "20px"
                }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                        flexWrap: "wrap"
                    }}>
                        <h4 style={{ color: "#333", margin: 0 }}>
                            📊 Voltage Measurements
                        </h4>
                        <div style={{ color: "#666", fontSize: "14px" }}>
                            <span>📅 Records: {chartData.length}</span>
                        </div>
                    </div>

                    {chartData.length > 0 ? (
                        <div
                            id="fuelVolumeChart"
                            style={{
                                width: "100%",
                                height: "500px",
                                backgroundColor: "#fff"
                            }}
                        ></div>
                    ) : (
                        <div style={{
                            textAlign: "center",
                            padding: "80px 20px",
                            color: "#666",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "8px"
                        }}>
                            <div style={{ fontSize: "48px", marginBottom: "20px" }}>⛽</div>
                            <h4>No voltage data found</h4>
                            <p>No external voltage, battery voltage or fuel voltage data found for the selected period.</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default VoltageGraph;